import { useSnackbar } from 'notistack';
import { useCallback, useEffect, useMemo } from 'react';
import { Control, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';

import {
  useLoggedInEmployeeId,
  useLoggedInEmployeeProfile,
} from '~/components/Auth';
import {
  LongAbsence,
  ShortAbsence,
  submitLongAbsence,
  submitShortAbsence,
} from '~/data/absences';
import { convertAbsenceEntryToAbsence } from '~/data/absences/data/mapping';
import {
  AbsenceEntry,
  AbsenceStatus,
  AbsenceType,
} from '~/data/absences/types';
import { useEmployeeAbsenceData } from '~/data/absences/useEmployeeAbsenceData';
import { useApplicationConfig } from '~/data/applicationConfig';
import { AbsenceDecision } from '~/features/AbsenceChanges/AbsenceDecisionDialog/data';
import { useDecisionRegistrySubForm } from '~/features/DecisionRegistry/DecisonRegistrySubForm';
import { useYupValidationResolver } from '~/hooks/useYupValidationResolver';
import { AbsencesTabContent_OnUpdateLegacy } from '~/library/AbsencesTabContent/component';

type Props = {
  employeeId: number;
  absenceId?: string;
  decision?: AbsenceDecision;
  onClose: () => void;
} & AbsencesTabContent_OnUpdateLegacy;

type AbsenceDecisionRegistryEntry = {
  decisionInfo: string;
  attachment?: File | null;
  reminderDate: Date | null;
};

type AbsenceDecisionForm = {
  status: AbsenceStatus;
  absenceId?: string;
  info?: string;
  addToDecisionRegistry: boolean;
  decisionRegistryEntry: AbsenceDecisionRegistryEntry;
};

type UseAbsenceDecisionDialogOutput = {
  isLoading: boolean;
  control: Control<AbsenceDecisionForm>;
  absenceEntry?: AbsenceEntry;
  onSubmit: () => void;
  decisionRegistry: {
    checkboxAvailable: boolean;
    showSubForm: boolean;
  };
};

export const useAbsenceDecisionDialog = (
  props: Props,
): UseAbsenceDecisionDialogOutput => {
  const { employeeId, absenceId, decision, onClose, onUpdateLegacy } = props;

  const { applicationConfig } = useApplicationConfig();
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  const loggedInEmployeeId = useLoggedInEmployeeId();
  const { firstName, lastName } = useLoggedInEmployeeProfile();

  const { absenceEntries, isLoading, mutate } = useEmployeeAbsenceData({
    employeeId,
  });

  const { handleSubmitDecisionRegistryEntry } = useDecisionRegistrySubForm({
    employeeId,
  });

  const {
    selectedAbsenceEntry,
    initialFormData,
    decisionRegistryCheckboxEnabled,
  } = useMemo(() => {
    const decisionRegistryEnabled =
      !!applicationConfig.fComConfig?.miscellaneousConfig
        ?.fComEnableDecisionRegistry;

    const foundEntry = !!absenceId
      ? absenceEntries.find((a) => a.uid === absenceId)
      : undefined;

    const getDecisionStatus = (decision: AbsenceDecision): AbsenceStatus => {
      switch (decision) {
        case AbsenceDecision.Approve:
          return AbsenceStatus.APPROVED;
        case AbsenceDecision.Reject:
          return AbsenceStatus.REJECTED;
        case AbsenceDecision.RequestChanges:
          return AbsenceStatus.CHANGES_REQUESTED;
      }
    };

    const initialFormData: AbsenceDecisionForm = {
      status: !!decision
        ? getDecisionStatus(decision)
        : foundEntry?.status ?? AbsenceStatus.DRAFT,
      absenceId,
      info: foundEntry?.info,
      addToDecisionRegistry: false,
      decisionRegistryEntry: {
        decisionInfo: '',
        reminderDate: null,
        attachment: undefined,
      },
    };

    const decisionRegistryCheckboxEnabled =
      decisionRegistryEnabled &&
      foundEntry?.type === AbsenceType.LONG &&
      !!decision &&
      [AbsenceDecision.Approve].includes(decision);

    return {
      selectedAbsenceEntry: foundEntry,
      initialFormData,
      decisionRegistryCheckboxEnabled,
    };
  }, [absenceEntries, absenceId, decision, applicationConfig]);

  const validationSchema = useMemo(() => {
    return yup.object().shape<AbsenceDecisionForm>({
      status: yup.string(),
      absenceId: yup.string(),
      info: yup.string(),
      addToDecisionRegistry: yup.boolean(),
      decisionRegistryEntry: yup
        .object()
        .shape<AbsenceDecisionRegistryEntry>({
          decisionInfo: yup.string(),
          reminderDate: yup.date().nullable().defined(),
          attachment: yup.object<File>().nullable(),
        })
        .defined(),
    });
  }, []);

  const resolver = useYupValidationResolver(
    validationSchema,
    'react-hook-form',
  );

  const { handleSubmit, control, reset, watch, getValues } =
    useForm<AbsenceDecisionForm>({
      defaultValues: initialFormData,
      resolver,
    });

  const showDecisionRegistryForm = watch('addToDecisionRegistry', false);

  useEffect(() => {
    reset(initialFormData);
  }, [initialFormData, reset]);

  const handleAbsenceUpdate = useCallback(
    (absenceEntry: AbsenceEntry) => {
      const { type, uid } = absenceEntry;

      const submissionProps = {
        employeeId,
        absenceId: uid ? uid.toString() : undefined,
      };

      const submissionData = convertAbsenceEntryToAbsence(absenceEntry);

      switch (type) {
        case AbsenceType.SHORT:
          return submitShortAbsence({
            ...submissionProps,
            data: submissionData as ShortAbsence,
          });
        case AbsenceType.LONG:
          return submitLongAbsence({
            ...submissionProps,
            data: submissionData as LongAbsence,
          });
      }
    },
    [employeeId],
  );

  const onSubmit = useMemo(
    () =>
      handleSubmit((data) => {
        const {
          status,
          info,
          absenceId,
          addToDecisionRegistry,
          decisionRegistryEntry,
        } = data;

        const attachmentFile = getValues('decisionRegistryEntry.attachment');

        if (
          !selectedAbsenceEntry ||
          selectedAbsenceEntry?.uid !== absenceId ||
          selectedAbsenceEntry.status === AbsenceStatus.DRAFT
        ) {
          return;
        }

        const submission: AbsenceEntry = {
          ...selectedAbsenceEntry,
          status,
          info,
          deciderEmpId: loggedInEmployeeId,
        };

        return handleAbsenceUpdate(submission).then(() => {
          if (addToDecisionRegistry) {
            const { decisionInfo, reminderDate } = decisionRegistryEntry;

            return handleSubmitDecisionRegistryEntry({
              type: 'Holiday',
              title: selectedAbsenceEntry.reason?.name,
              maker: `${firstName} ${lastName}`,
              info: decisionInfo,
              reminderDate,
              file: attachmentFile,
              isSystemGenerated: false,
            })
              .then(() => {
                enqueueSnackbar(t('label_add_to_dr_list_saved'), {
                  variant: 'success',
                });
                onClose();
              })
              .catch(() => {
                enqueueSnackbar(
                  `${t('laFailedToSave')}: ${t('laDecisionRegistry')}`,
                  {
                    variant: 'error',
                  },
                );
              })
              .finally(() => {
                mutate();
                if (onUpdateLegacy) {
                  onUpdateLegacy(['absence-decision']);
                }
              });
          } else {
            mutate();
            if (onUpdateLegacy) {
              onUpdateLegacy(['absence-decision']);
            }
            onClose();
          }
        });
      }),
    [
      loggedInEmployeeId,
      selectedAbsenceEntry,
      handleSubmit,
      handleAbsenceUpdate,
      handleSubmitDecisionRegistryEntry,
      getValues,
      firstName,
      lastName,
      onClose,
      mutate,
      t,
      enqueueSnackbar,
      onUpdateLegacy,
    ],
  );

  return {
    isLoading,
    absenceEntry: selectedAbsenceEntry,
    onSubmit,
    control,
    decisionRegistry: {
      checkboxAvailable: decisionRegistryCheckboxEnabled,
      showSubForm: showDecisionRegistryForm,
    },
  };
};
